import React from 'react';
import {
  XMarkIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

export interface KeystoneSubmitRow {
  invoice_number: string;
  success: boolean;
  step?: string;
  status_code: number | null;
  result_key?: string;
  job_key?: string;
  pdf_attached?: boolean;
  response?: unknown;
  detail?: string;
}

interface Props {
  isOpen: boolean;
  destination: string;
  results: KeystoneSubmitRow[];
  /** Called with the invoice_number when a rejected row is clicked. Host
   *  typically closes the modal, switches the sidebar to the Errors tab,
   *  and selects that invoice so the clerk can fix it. */
  onViewInvoice?: (invoiceNumber: string) => void;
  onClose: () => void;
}

/** Walk Keystone's error shape (`{errors: [...], success: false, ...}` or
 *  the raw text when they return non-JSON) and pull a flat string list we
 *  can show to the clerk. */
function extractErrors(response: unknown): string[] {
  if (!response || typeof response !== 'object') {
    return [];
  }
  const r = response as Record<string, unknown>;
  const out: string[] = [];
  if (Array.isArray(r.errors)) {
    for (const e of r.errors) out.push(String(e));
  }
  if (typeof r.error === 'string') out.push(r.error);
  if (typeof r.detail === 'string') out.push(r.detail);
  if (typeof r._raw === 'string') out.push(r._raw);
  return out;
}

/** Human-readable summary of the HTTP status we got from Keystone, used
 *  when the response body has no useful error text. */
function fallbackStatusMessage(step: string | undefined, status: number | null): string {
  if (status === null) {
    return 'Could not reach Keystone (network or timeout). Try again in a moment.';
  }
  if (status === 401 || status === 403) {
    return 'Keystone rejected the request as unauthorized. Our API key may need to be updated.';
  }
  if (status === 404) {
    return 'Keystone endpoint responded with Not Found. The configured write-back URL may be wrong.';
  }
  if (status === 422) {
    return 'Keystone found a validation problem but did not report a field. Check the invoice data.';
  }
  if (status >= 500) {
    return 'Keystone had a server error. Try again shortly; if it keeps failing, flag it.';
  }
  return `${step || 'Request'} rejected with HTTP ${status}.`;
}

const KeystoneSubmitResultsModal: React.FC<Props> = ({ isOpen, destination, results, onViewInvoice, onClose }) => {
  const [expandedKey, setExpandedKey] = React.useState<string | null>(null);

  // Auto-expand the first failure each time this modal is reopened with a
  // fresh set of results, so the clerk sees the reason without an extra click.
  React.useEffect(() => {
    if (!isOpen) return;
    const firstFail = results.find(r => !r.success);
    setExpandedKey(firstFail ? `${firstFail.invoice_number}-fail` : null);
  }, [isOpen, results]);

  if (!isOpen) return null;

  const okCount = results.filter(r => r.success).length;
  const failCount = results.length - okCount;
  const anyFailed = failCount > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div
              className={`p-2 rounded-lg ${
                anyFailed ? 'bg-red-100 dark:bg-red-900/30' : 'bg-emerald-100 dark:bg-emerald-900/30'
              }`}
            >
              {anyFailed ? (
                <XCircleIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
              ) : (
                <CheckCircleIcon className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Submit to Keystone — {anyFailed ? 'needs attention' : 'done'}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {okCount} of {results.length} submitted
                {failCount > 0 && `, ${failCount} rejected`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Rows */}
        <div className="px-6 py-4 max-h-[28rem] overflow-y-auto space-y-2">
          {results.map((row, i) => {
            const rowKey = `${row.invoice_number}-${row.success ? 'ok' : 'fail'}-${i}`;
            const isFailure = !row.success;
            const isExpanded = expandedKey === rowKey;
            const errors = extractErrors(row.response);
            const primaryError = errors[0] || fallbackStatusMessage(row.step, row.status_code);

            return (
              <div
                key={rowKey}
                className={`rounded-lg border overflow-hidden ${
                  row.success
                    ? 'border-emerald-200 dark:border-emerald-900/40 bg-emerald-50/60 dark:bg-emerald-900/10'
                    : 'border-red-200 dark:border-red-900/40 bg-red-50/60 dark:bg-red-900/10'
                }`}
              >
                {/* Summary row — always visible, click to expand failures */}
                <button
                  type="button"
                  onClick={isFailure ? () => setExpandedKey(isExpanded ? null : rowKey) : undefined}
                  disabled={!isFailure}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left ${
                    isFailure ? 'hover:bg-red-100/60 dark:hover:bg-red-900/20 cursor-pointer' : 'cursor-default'
                  }`}
                >
                  {row.success ? (
                    <CheckCircleIcon className="h-5 w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                  ) : (
                    <XCircleIcon className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <span className="font-mono text-sm font-semibold text-gray-900 dark:text-white break-all">
                        {row.invoice_number}
                      </span>
                      {row.success && row.result_key && (
                        <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                          key: {row.result_key}
                          {row.pdf_attached === true && ' • PDF attached'}
                        </span>
                      )}
                      {isFailure && (
                        <span className="text-xs uppercase tracking-wide text-red-700 dark:text-red-300 font-semibold">
                          {row.step === 'validation'
                            ? 'blocked by KeyScan'
                            : (
                              <>
                                rejected
                                {row.step && ` at ${row.step} step`}
                                {row.status_code != null && ` • HTTP ${row.status_code}`}
                              </>
                            )}
                        </span>
                      )}
                    </div>
                    {isFailure && !isExpanded && (
                      <p className="text-xs text-gray-700 dark:text-gray-300 mt-1 truncate">
                        {primaryError}
                      </p>
                    )}
                  </div>
                  {isFailure && (
                    <span className="text-xs text-red-700 dark:text-red-300 font-medium flex-shrink-0">
                      {isExpanded ? 'Hide details' : 'Show details'}
                    </span>
                  )}
                </button>

                {/* Expanded detail — only for failures */}
                {isFailure && isExpanded && (
                  <div className="px-3 pb-3 pt-1 space-y-2 border-t border-red-200 dark:border-red-900/40 bg-red-50/40 dark:bg-red-900/5">
                    {errors.length > 0 ? (
                      <div>
                        <p className="text-xs font-semibold text-red-800 dark:text-red-300 uppercase tracking-wide mb-1 mt-2">
                          {row.step === 'validation' ? 'What needs to be fixed' : 'Keystone said'}
                        </p>
                        <ul className="space-y-1">
                          {errors.map((e, idx) => (
                            <li
                              key={idx}
                              className="text-sm text-gray-800 dark:text-gray-200 break-words flex gap-2"
                            >
                              <span className="text-red-600 dark:text-red-400 mt-0.5">•</span>
                              <span>{e}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">{primaryError}</p>
                    )}

                    {row.detail && errors.length === 0 && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 italic">{row.detail}</p>
                    )}

                    {onViewInvoice && (
                      <div className="pt-1">
                        <button
                          type="button"
                          onClick={() => onViewInvoice(row.invoice_number)}
                          className="px-3 py-1.5 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
                        >
                          Open this invoice to fix it &rarr;
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <p className="text-xs text-gray-500 dark:text-gray-400 font-mono truncate max-w-[70%]" title={destination}>
            {destination}
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default KeystoneSubmitResultsModal;
